import * as SentryBrowser from "@sentry/browser";
import * as Sentry from "@sentry/react";
import { QueryClient } from "@tanstack/react-query";
import i18nProvider from "i18n/i18nProvider";
import authProvider from "misc/authProvider";
import dataProvider from "misc/dataProvider";
import { BackendPermissionsType } from "misc/permission.types";
import { pageTitle, sentryDsn, sentryEnvironment } from "misc/settings";
import { mergedTheme } from "misc/theming";
import { useEffect } from "react";
import { Admin, LayoutComponent } from "react-admin";
import EdmError from "templates/EdmError";
import EdmLayout from "templates/EdmLayout";
import EdmNotFound from "templates/EdmNotFound";
import LoginPage from "templates/LoginPage";

Sentry.init({
  dsn: sentryDsn,
  integrations: [SentryBrowser.browserTracingIntegration],
  tracesSampleRate: 0.2,
  environment: sentryEnvironment,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const determineResources = async (permissions: BackendPermissionsType) => {
  if (permissions.isStaff) {
    return (await import("./resources/staffResources")).default;
  }
  if (permissions.isLecturer) {
    return (await import("./resources/lecturerResources")).default;
  }
  if (permissions.isAttendee) {
    return (await import("./resources/attendeeResources")).default;
  }
  return (await import("./resources/publicResources")).default;
};

export default function App() {
  useEffect(() => {
    document.title = pageTitle;
  }, []);

  return (
    <Admin
      i18nProvider={i18nProvider}
      authProvider={authProvider}
      dataProvider={dataProvider}
      loginPage={LoginPage}
      layout={EdmLayout as LayoutComponent}
      theme={mergedTheme}
      disableTelemetry
      queryClient={queryClient}
      error={EdmError}
      catchAll={EdmNotFound}
    >
      {determineResources}
    </Admin>
  );
}
